import React from 'react';

import { ResponsiveTable } from '@src/components/Table';
import {
  ChartControls,
  DistributionDonut,
  DrilldownDrawer,
  DRILLDOWN_TABLE_MAX_HEIGHT,
  MetricsPanel,
  PercentTrendChart,
  ProgressDistributionList,
  RankingPanel,
  ReportGrid,
  ReportSection,
  SectionStatusContent,
  SeriesTotals,
  SeriesTrendChart,
  TimeMetricsGrid,
} from '@src/packages/dashboard/components/ReportSummary';
import HistoryWarnings from './HistoryWarnings';
import { useTasksSummary } from './hooks/useTasksSummary';
import styles from './styles.module.less';
import type { TasksSummaryProps } from './types';

const TasksSummary: React.FC<TasksSummaryProps> = props => {
  const {
    kpis,
    evolution,
    incidents,
    periodResult,
    taskCompliance,
    userDistribution,
    timeMetrics,
    rankings,
    drilldown,
    warnings,
  } = useTasksSummary(props);

  return (
    <ReportGrid>
      <MetricsPanel {...kpis} />
      <HistoryWarnings warnings={warnings} />

      <ReportSection
        title={evolution.title}
        description={evolution.description}
        headerExtra={<ChartControls {...evolution.controls} />}
        status={evolution.status}
      >
        <PercentTrendChart {...evolution.chart} />
      </ReportSection>

      <ReportGrid layout="twoColumns">
        <ReportSection
          title={periodResult.title}
          description={periodResult.description}
          className={styles.fillSection}
          status={periodResult.status}
        >
          <DistributionDonut
            items={periodResult.items}
            centerContent={periodResult.centerContent}
            ariaLabel={periodResult.ariaLabel}
          />
        </ReportSection>
        <ReportSection
          title={taskCompliance.title}
          description={taskCompliance.description}
          status={taskCompliance.status}
        >
          <ProgressDistributionList items={taskCompliance.items} />
        </ReportSection>
      </ReportGrid>

      <DrilldownDrawer {...drilldown.drawer} pagination={drilldown.pagination}>
        <SectionStatusContent status={drilldown.status}>
          {drilldown.table?.kind === 'users' && (
            <ResponsiveTable
              columns={drilldown.table.columns}
              dataSource={drilldown.table.dataSource}
              dataResetKey={drilldown.table.dataResetKey}
              loading={drilldown.loading}
              type="light"
              stickyHeader
              maxHeight={DRILLDOWN_TABLE_MAX_HEIGHT}
              wrapperClassName={styles.drilldownTable}
              data-testid="drilldown-table"
            />
          )}
          {drilldown.table?.kind === 'orders' && (
            <ResponsiveTable
              columns={drilldown.table.columns}
              dataSource={drilldown.table.dataSource}
              dataResetKey={drilldown.table.dataResetKey}
              loading={drilldown.loading}
              type="light"
              stickyHeader
              maxHeight={DRILLDOWN_TABLE_MAX_HEIGHT}
              wrapperClassName={styles.drilldownTable}
              data-testid="drilldown-table"
            />
          )}
        </SectionStatusContent>
      </DrilldownDrawer>

      <ReportSection
        title={incidents.title}
        description={incidents.description}
        headerExtra={<ChartControls {...incidents.controls} />}
        status={incidents.status}
      >
        <SeriesTotals items={incidents.totals} />
        <SeriesTrendChart {...incidents.chart} />
      </ReportSection>

      <ReportGrid layout="twoColumns">
        {timeMetrics.map(group => (
          <ReportSection key={group.key} title={group.title} description={group.description} status={group.status}>
            <TimeMetricsGrid items={group.items} />
          </ReportSection>
        ))}
      </ReportGrid>

      <ReportGrid layout="twoColumns">
        {rankings.map(({ key, ...ranking }) => (
          <RankingPanel key={key} {...ranking} />
        ))}
      </ReportGrid>

      <ReportSection
        title={userDistribution.title}
        description={userDistribution.description}
        status={userDistribution.status}
      >
        <ProgressDistributionList items={userDistribution.items} variant="wide" />
      </ReportSection>
    </ReportGrid>
  );
};

export default TasksSummary;
